import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { Vector3 } from 'three';
import { CompositeArticleContainer } from './composite-article.container';
import { CompositeArticleUtils } from '@article/util/composite-article-utils';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ConnectionAreaUsageVO } from '@shared/vo/meta-element.vo';
import { ConnectionLevelUtils } from '@project/cae/util/connection-level-utils';
import { MetaElementUsageUtils } from '@project/util/meta-element-usage-utils';
import { ContainerUtils } from '@3d-scene/model3d/container.utils';
import { Container } from '@3d-scene/model3d/container';
import { ArticleContainer } from '@3d-scene/model3d/article-container';

export class CompositeArticleContainers {

  public containers!: Container[];

  constructor(private containerService: ContainerService,
              private compositeArticleUtils: CompositeArticleUtils) {
    this.reload();
  }

  private enumerateArticleContainers(): Container[] {
    const containers: Container[] = [];
    const articleContainers = this.containerService.enumerateArticleContainersInGlobal();
    ArticleContainerUtils.sortArticleContainersByDistanceOfBBox(articleContainers, new Vector3());
    const compositeArticleContainersByArticleIdentity = new Map<string, CompositeArticleContainer>();
    for (const articleContainer of articleContainers) {
      const compositeArticleUsageId = this.compositeArticleUtils.getCompositeArticleUsageIdsFromUsageByIndex(articleContainer.articleUsage, 0);
      const compositeArticle = this.compositeArticleUtils.getCompositeArticle(articleContainer);
      if (compositeArticleUsageId && compositeArticle) {
        let compositeArticleContainer = compositeArticleContainersByArticleIdentity.get(compositeArticleUsageId);
        if (compositeArticleContainer) {
          compositeArticleContainer.addArticleContainer(articleContainer);
        } else {
          compositeArticleContainer = new CompositeArticleContainer(compositeArticle);
          compositeArticleContainer.addArticleContainer(articleContainer);
          compositeArticleContainersByArticleIdentity.set(compositeArticleUsageId, compositeArticleContainer);
          containers.push(compositeArticleContainer);
        }
      } else {
        containers.push(articleContainer);
      }
    }
    return containers;
  }

  public findContainer(articleUsageId: string): Container | undefined {
    for (const container of this.containers) {
      if (ContainerUtils.isArticleContainer(container)) {
        if (container.articleUsage.id === articleUsageId) {
          return container;
        }
      } else {
        const foundContainer = container.articleContainers.find((articleContainer) => articleContainer.articleUsage.id === articleUsageId);
        if (foundContainer) {
          return container;
        }
      }
    }
  }

  public getChildrenRecursive(container: Container, options: { level?: number } = {}): Container[] {
    return this.getChildren(container, {...options, recursive: true});
  }

  public getChildren(container: Container, options: { level?: number, recursive?: boolean } = {}): Container[] {
    const forwardConnectionAreaUsages = ContainerUtils.articleContainers(container).flatMap((articleContainer) => this.forwardConnectionsOfLevel(articleContainer, options.level));
    const forwardUsageIds = forwardConnectionAreaUsages.flatMap(MetaElementUsageUtils.forwardConnectedArticleUsageIds);
    const relatedContainers: Container[] = [];
    for (const forwardUsageId of forwardUsageIds) {
      const relatedContainer = this.findContainer(forwardUsageId);
      if (relatedContainer && relatedContainer != container && !relatedContainers.includes(relatedContainer)) {
        relatedContainers.push(relatedContainer);
        if (options.recursive) {
          relatedContainers.push(...this.getChildren(relatedContainer, options));
        }
      }
    }
    return relatedContainers;
  }

  public reload() {
    this.containers = this.enumerateArticleContainers();
  }

  private forwardConnectionsOfLevel(articleContainer: ArticleContainer, level: number | undefined) {
    const forwardConnectionAreaUsages = ArticleUsageUtils.forwardConnectionAreaUsages(articleContainer.articleUsage);
    if (level === undefined) {
      return forwardConnectionAreaUsages;
    }

    const forwardConnectionAreaUsagesWithLevel: ConnectionAreaUsageVO[] = [];
    for (const forwardConnectionAreaUsage of forwardConnectionAreaUsages) {
      const connection = articleContainer.findConnectionByName(forwardConnectionAreaUsage.identity.metaElementName);
      if (connection) {
        if (ConnectionLevelUtils.getConnectionLevelByProperty(connection.metaElementVO) === level) {
          forwardConnectionAreaUsagesWithLevel.push(forwardConnectionAreaUsage);
        }
      }
    }
    return forwardConnectionAreaUsagesWithLevel;
  }
}
